import {
  ButtonSize,
  ButtonColor,
  ButtonProps,
} from '@components/common/button/types';
import LoadingAccent from '@assets/svgs/LoadingAccent.svg';
import LoadingWhite from '@assets/svgs/LoadingWhite.svg';

const buttonShapes: {
  [T in ButtonSize]: (isRect: boolean) => string[];
} = {
  small: (isRect) => ['h-[32px]', isRect ? 'rounded-[8px]' : 'rounded-[16px]'],
  medium: (isRect) => ['h-[40px]', isRect ? 'rounded-[8px]' : 'rounded-[20px]'],
  large: (isRect) => ['h-[48px]', isRect ? 'rounded-[8px]' : 'rounded-[24px]'],
};

const buttonColors: { [T in ButtonColor]: (loading: boolean) => string[] } = {
  primary: (loading) => [
    'content_primary_inverse',
    loading ? 'surface_primary_inverse_active' : 'surface_primary_inverse',
    'hover:surface_primary_inverse_active',
    'active:surface_primary_inverse_active',
    'disabled:surface_disabled',
  ],
  secondary: (loading) => [
    'content_primary_inverse',
    loading ? 'surface_accent_active' : 'surface_accent',
    'hover:surface_accent_active',
    'active:surface_accent_active',
    'disabled:surface_disabled',
  ],
  tertiary: (loading) => [
    'content_secondary',
    loading ? 'surface_primary_active' : 'surface_tertiary',
    'hover:surface_primary_active',
    'active:surface_primary_active',
    'disabled:surface_disabled',
  ],
  outline: (loading) => [
    'content_secondary',
    ...(loading
      ? ['surface_tertiary']
      : ['surface_primary', 'border', 'border-solid', 'border_black_opacity']),
    'hover:surface_tertiary',
    'hover:border_secondary',
    'active:surface_tertiary',
    'active:border_secondary',
    'disabled:surface_primary',
    'disabled:border_black_opacity',
    'disabled:border',
    'disabled:border-solid',
  ],
  kakao: (loading) => [
    'content_secondary',
    loading ? 'bg-[#FFDE30]' : 'bg-[#FFEA2B]',
    'hover:bg-[#FFDE30]',
    'active:bg-[#FFDE30]',
  ],
};

const buttonPadding = (
  loading: boolean,
  size: ButtonSize,
  color: ButtonColor,
) => {
  if (color !== 'outline' || loading) {
    return size === 'large' ? 'px-[20px]' : 'px-[16px]';
  } else {
    return size === 'large' ? 'px-[19px]' : 'px-[15px]';
  }
};

export default function Button({
  type,
  shape,
  size,
  color,
  width,
  disabled,
  loading,
  fullWidth,
  buttonStyle,
  disableStyle = true,
  children,
  onClick,
}: ButtonProps) {
  const getClassNames = () => {
    return [
      buttonStyle,
      ...buttonShapes[size](shape === 'rect'),
      ...buttonColors[color](loading),
      buttonPadding(loading, size, color),
      fullWidth ? 'w-full' : width,
      size === 'large' && 'min-w-[88px]',
      disableStyle ? 'disabled:content_disabled' : '',
    ].join(' ');
  };

  const onButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      return;
    }

    onClick && onClick(event);
  };

  const LoadingIcon = color === 'secondary' ? LoadingWhite : LoadingAccent;

  return (
    <button
      type={type}
      disabled={disabled}
      className={`relative flex justify-center items-center font_button_bold_md ${getClassNames()}`}
      onClick={onButtonClick}
    >
      {!loading ? (
        children
      ) : (
        <LoadingIcon className="animate-spin w-[16px] h-[16px]" />
      )}
    </button>
  );
}

Button.defaultProps = {
  type: 'button',
  status: 'normal',
  disabled: false,
  loading: false,
};
